import { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const hasCheckedRef = useRef(false);

  // ✅ CRITICAL: Check session only ONCE on mount
  useEffect(() => {
    // Prevent multiple checks
    if (hasCheckedRef.current) {
      return;
    }
    
    hasCheckedRef.current = true;
    let isMounted = true;

    const checkSession = async () => {
      try {
        console.log(`🔐 [ProtectedRoute] Checking session for: ${location.pathname}`);
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        if (error) {
          console.error('❌ [ProtectedRoute] Session error', error);
          setHasSession(false);
          setIsChecking(false);
          return;
        }

        if (session?.user) {
          console.log('✅ [ProtectedRoute] Session found', {
            user_id: session.user.id,
            email: session.user.email
          });
          setHasSession(true);
        } else {
          console.log('⚠️ [ProtectedRoute] No session found');
          setHasSession(false);
        }
        
        setIsChecking(false);
      } catch (err: any) {
        if (!isMounted) return;
        console.error('❌ [ProtectedRoute] Check failed', err);
        setHasSession(false);
        setIsChecking(false);
      }
    };

    checkSession();

    return () => {
      isMounted = false;
    };
  }, []); // ✅ CRITICAL: Empty dependency array - run ONCE on mount

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-3xl text-teal-600 animate-spin"></i>
          </div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if no session
  if (!hasSession) {
    console.log(`⚠️ [ProtectedRoute] Redirecting to login from: ${location.pathname}`);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
